const stringifyAndTrim = (value) => {
  return String(value ?? '').trim();
};

const VALID_PLACEHOLDERS = [
  '{name}',
  '{part}',
  '{operation}',
  '{op}',
  '{lot}',
  '{description}',
];

/**
 * Validates if a format string contains at least one valid placeholder.
 * Used to determine if a display name format is valid or if it contains unresolved placeholders.
 *
 * @param {string} fmt - The format string to validate
 * @returns {boolean} True if format contains at least one valid placeholder
 */
export const validateJobDisplayNameFormat = (fmt) => {
  const trimmed = stringifyAndTrim(fmt);
  return (
    trimmed.length > 0 &&
    VALID_PLACEHOLDERS.some((p) => {
      return trimmed.includes(p);
    })
  );
};

/**
 * Resolves placeholder values from a work order operation object.
 * Extracts name, part, operation, lot, and description from various nested properties.
 *
 * @param {Object} orderOp - Work order operation object
 * @returns {Object} Resolved values for all placeholders
 */
const resolveValues = (orderOp) => {
  const partOp = orderOp.partOperation ?? orderOp.matchingPartOperations?.[0];
  const { workOrder } = orderOp;

  const part = stringifyAndTrim(
    orderOp.partNumber ??
      orderOp.partName ??
      workOrder?.partNumber ??
      partOp?.partNumber ??
      partOp?.partName
  );

  const name = stringifyAndTrim(
    orderOp.name ?? workOrder?.workOrderId ?? orderOp.workOrderId
  );

  const operation = stringifyAndTrim(
    orderOp.sequenceNumber ?? orderOp.operationNum ?? partOp?.sequenceNumber
  );

  const lot = stringifyAndTrim(orderOp.lot ?? workOrder?.lot ?? partOp?.lot);

  const description = stringifyAndTrim(
    orderOp.description ?? partOp?.description ?? workOrder?.description
  );

  return {
    name,
    part,
    operation,
    op: operation,
    lot,
    description,
  };
};

/**
 * Formats a work order operation display name using a format template.
 * Falls back to default formats if the provided format is invalid or placeholders can't be resolved.
 *
 * @param {Object} orderOp - Work order operation object
 * @param {string|null} jobDisplayNameFormat - Format template with placeholders (e.g., '{part} Op {operation}')
 * @returns {string|null} Formatted display name, or null if format doesn't match available data
 *
 * @example
 * formatOrderOpName(orderOp, '{part} Op {operation}')
 * // Returns: "ABC-123 Op 10"
 */
export const formatOrderOpName = (orderOp, jobDisplayNameFormat = null) => {
  if (!orderOp) return '';

  if (!validateJobDisplayNameFormat(jobDisplayNameFormat)) {
    return (
      formatOrderOpName(orderOp, '{part} Op {operation}') ??
      formatOrderOpName(orderOp, '{name} Op {operation}') ??
      formatOrderOpName(orderOp, '{part}') ??
      formatOrderOpName(orderOp, '{name}') ??
      ''
    );
  }

  const values = resolveValues(orderOp);

  const usedPlaceholders = jobDisplayNameFormat.match(/\{(\w+)\}/g) || [];
  const allResolved = usedPlaceholders.every((placeholder) => {
    const key = placeholder.slice(1, -1);
    return values[key]?.length > 0;
  });

  if (!allResolved) {
    // Treat formats with unresolved placeholders as invalid custom formats.
    // For custom formats, fall back to the default formats.
    const isDefaultFormat =
      jobDisplayNameFormat === '{part} Op {operation}' ||
      jobDisplayNameFormat === '{name} Op {operation}' ||
      jobDisplayNameFormat === '{part}' ||
      jobDisplayNameFormat === '{name}';

    if (!isDefaultFormat) {
      return (
        formatOrderOpName(orderOp, '{part} Op {operation}') ??
        formatOrderOpName(orderOp, '{name} Op {operation}') ??
        formatOrderOpName(orderOp, '{part}') ??
        formatOrderOpName(orderOp, '{name}') ??
        ''
      );
    }

    // If even a default format cannot be fully resolved, return an empty string.
    return '';
  }

  const result = jobDisplayNameFormat
    .replace(/\{name\}/g, values.name)
    .replace(/\{part\}/g, values.part)
    .replace(/\{operation\}/g, values.operation)
    .replace(/\{op\}/g, values.op)
    .replace(/\{lot\}/g, values.lot)
    .replace(/\{description\}/g, values.description)
    .trim();

  return result || '';
};
